/**
 * The MFA methods the front end can take a user through, i.e. the availability
 * map the setup screen decides the selectable options from.
 *
 * NOTE: the API reports the methods a user is allowed to enroll with, and that
 * list is wider than what the front end implements. `TOTP` is the only method
 * of phase 1, so every other allowed method is rendered as unavailable until
 * its own flow ships.
 *
 * @type {Object}
 */
export const MFA_METHODS = {
    TOTP: 'TOTP',
};

/**
 * The `status` values of an enrolled MFA method, as reported on the user's
 * `mfaMethods` list.
 * `ACTIVE` -> the method was verified and is used for challenges.
 *
 * @type {Object}
 */
export const MFA_METHOD_STATUS = {
    ACTIVE: 'ACTIVE',
};

/**
 * The date format of the "Added" line of each MFA method of the profile MFA
 * configuration page.
 *
 * NOTE: date-fns is v1, so the tokens are the v1 ones (`D`, `YYYY`), not the
 * v2 ones (`d`, `yyyy`).
 *
 * @type {string}
 */
export const MFA_PROFILE_DATE_FORMAT = 'MMMM D, YYYY';

/**
 * The copy of the profile MFA configuration page.
 *
 * NOTE: the `BANNERS` copy is the ticket's proposed copy, final wording to be
 * confirmed with UX.
 *
 * @type {Object}
 */
export const MFA_PROFILE = {
    TITLE: 'MFA Configuration',
    SUBTITLE: 'Add one or more methods to secure your account.',
    POLICY_DESCRIPTION: [
        'Multi-Factor Authentication (MFA) Status for all platform users. Below is the specific effects of the selected enrollment level:',
        'When this option is enabled the following rules apply:',
    ],
    ACTIVE_METHODS_TITLE: 'Active Methods',
    ACTIVE_METHODS_SUBTITLE: 'Your primary method is used by default at every login.',
    ADD_METHOD: 'Add a Method',
    ADD_METHOD_DISABLED_TOOLTIP: 'You already have an authenticator app set up.',
    PRIMARY_BADGE: 'Primary',
    REMOVE: 'Remove',
    ADDED_PREFIX: 'Added',
    METHODS_COUNT_SINGULAR:
        'You have ${COUNT} method configured. The primary method is used first at login.',
    METHODS_COUNT_PLURAL:
        'You have ${COUNT} methods configured. The primary method is used first at login.',
    EMPTY_TITLE: 'No authentication methods configured',
    EMPTY_SUBTITLE:
        'Set up multi-factor authentication to add an extra layer of security to your account.',
    BACK_CAPTION: 'Login & Security',
    BANNERS: {
        REQUIRED_NOT_ENROLLED:
            'Your organization requires multi-factor authentication. Add a method to keep access to your account.',
        REQUIRED_ENROLLED: 'Multi-factor authentication is required by your organization.',
        OPTIONAL:
            'Multi-factor authentication is available for your organization. We recommend adding a method to protect your account.',
        OFF: "Multi-factor authentication isn't enabled for your organization.",
    },
};

/**
 * The `status` values returned by `POST /v2/oauth/authenticate` when MFA is involved.
 * `MFA_ENROLLED` -> the user has a method enrolled and must verify a code.
 * `MFA_REQUIRED` -> the user has no method enrolled and must set one up first.
 *
 * @type {Object}
 */
export const MFA_STATUS = {
    MFA_ENROLLED: 'MFA_ENROLLED',
    MFA_REQUIRED: 'MFA_REQUIRED',
};

/**
 * The `mfaPolicy` values of a company or a partner.
 * `REQUIRED` -> every user has to set up and use MFA.
 * `OPTIONAL` -> users are encouraged to use MFA, applied when no policy is set.
 * `OFF`      -> MFA is not enforced.
 *
 * @type {Object}
 */
export const MFA_POLICY = {
    REQUIRED: 'REQUIRED',
    OPTIONAL: 'OPTIONAL',
    OFF: 'OFF',
};

/**
 * The copy of the MFA policy settings screen.
 *
 * @type {Object}
 */
export const MFA_POLICY_SETTINGS = {
    BLOCKING_COMPANIES_TRUNCATED: 'Additional companies are not listed.',
    DESCRIPTION: 'MFA is relevant for just direct login users SSO users will not be affected.',
    ENROLLED_USERS: '${count} enrolled user(s)',
    LEVELS_DESCRIPTION: [
        'Multi-Factor Authentication (MFA) Status and Options for all platform users. Below are the specific effects of each enrollment level:',
        'When this option is enabled, for each level, the following rules apply:',
    ],
    LEVELS_FOOTER: 'Create, modify, and delete MFA settings and configurations.',
    OFF_WARNING:
        'Turning MFA off means users will no longer be forced to use multi-factor authentication when signing in.',
    SKU_WARNING:
        'You are changing the MFA policy to ${POLICY}. However, one or more companies under your account have a SKU that requires MFA to be enabled.',
    SUCCESS: 'MFA policy settings have been saved successfully.',
    TITLE: 'Multi-Factor Authentication Policy Configuration and Status',
    PARTNER_REQUIRED: 'MFA is required by your partner. This setting cannot be changed.',
    PARTNER_OFF: 'MFA has been disabled by your partner. This setting cannot be changed.',
};

/**
 * The display copy of each MFA policy, in the order the options are rendered.
 *
 * @type {Array}
 */
export const MFA_POLICY_OPTIONS = [
    {
        description: 'All users are forced to set up and use MFA. Access is blocked without it.',
        label: 'Required',
        value: MFA_POLICY.REQUIRED,
    },
    {
        description: 'Users are encouraged to use MFA, but can access without it.',
        label: 'Optional',
        value: MFA_POLICY.OPTIONAL,
    },
    {
        description: 'MFA is not enforced, and existing setups are ignored.',
        label: 'Off',
        value: MFA_POLICY.OFF,
    },
];

/**
 * The display copy of each `reason` a company blocks a partner policy downgrade with.
 *
 * @type {Object}
 */
export const MFA_POLICY_BLOCKING_REASONS = {
    OPTIONAL_WITH_ENROLLMENTS: 'MFA policy is Optional and users are enrolled',
    POLICY_REQUIRED: 'MFA policy is Required',
};

/**
 * The `status` values that require the user to complete an MFA flow before signing in.
 *
 * @type {Array}
 */
export const MFA_REQUIRED_STATUSES = [MFA_STATUS.MFA_ENROLLED, MFA_STATUS.MFA_REQUIRED];

/**
 * The routes of the MFA screens.
 * `ENROLLMENT_SUCCESS` -> where a completed enrollment is confirmed before the
 *                         sign in is completed.
 * `RECOVERY`        -> where a user who cannot produce a code finds their options.
 * `SCAN_QR_CODE`    -> where the QR code and the key of a started TOTP enrollment
 *                      are scanned, or typed, into an authenticator app.
 * `SETUP`           -> where a user without an enrolled method sets one up, i.e.
 *                      the enrollment gate of the sign in flow.
 * `VERIFY_ENROLLMENT` -> where the code of a started TOTP enrollment is entered
 *                        to activate the method, i.e. the last enrollment step.
 * `VERIFY_IDENTITY`   -> where an enrolled user enters the verification code.
 *
 * @type {Object}
 */
export const MFA_ROUTES = {
    ENROLLMENT_FAILED: '/auth/mfa/enrollment-failed',
    ENROLLMENT_SUCCESS: '/auth/mfa/enrollment-success',
    RECOVERY: '/auth/mfa/recovery',
    SCAN_QR_CODE: '/auth/mfa/scan-qr-code',
    SETUP: '/auth/mfa-enroll',
    VERIFY_ENROLLMENT: '/auth/mfa/verify-enrollment',
    VERIFY_IDENTITY: '/auth/mfa/verify-identity',
};

/**
 * The MFA route the user is sent to for each authenticate `status`.
 *
 * @type {Object}
 */
export const MFA_ROUTE_BY_STATUS = {
    [MFA_STATUS.MFA_ENROLLED]: MFA_ROUTES.VERIFY_IDENTITY,
    [MFA_STATUS.MFA_REQUIRED]: MFA_ROUTES.SETUP,
};

/**
 * The keys of the login credentials that are needed to complete the
 * post sign in redirect once the MFA code has been verified.
 *
 * NOTE: the user's password is never part of this list.
 *
 * @type {Array}
 */
export const MFA_LOGIN_CONTEXT_KEYS = [
    'redirect',
    'redirectState',
    'reload',
    'rememberUsername',
    'username',
];

/**
 * The number of digits of a TOTP verification code.
 *
 * @type {number}
 */
export const MFA_CODE_LENGTH = 6;

/**
 * The copy of the MFA verify identity screen.
 *
 * @type {Object}
 */
export const MFA_VERIFY_IDENTITY = {
    BACK: 'Back',
    CANT_ACCESS_CODE: 'Can’t access your code?',
    CODE_INPUT_LABEL: 'Digit DIGIT_POSITION of MFA verification code',
    SUBTITLE: 'Enter the 6-digit code from your authenticator app.',
    TITLE: 'Verify Your Identity',
    VERIFY: 'Verify',
};

/**
 * The copy of the MFA verify enrollment screen, the last step of the TOTP
 * enrollment where the code of the freshly added authenticator entry is
 * verified to activate the method.
 *
 * @type {Object}
 */
export const MFA_VERIFY_ENROLLMENT = {
    BACK: 'Back',
    SUBTITLE: 'Enter the 6-digit code shown in your authenticator app to finish setup.',
    TITLE: 'Enter Verification Code',
    VERIFY: 'Verify and Activate',
};

/**
 * The copy of the MFA enrollment success screen, the confirmation rendered once
 * the verified code has activated the method. `METHOD` is replaced with the
 * title of the enrolled method.
 *
 * @type {Object}
 */
export const MFA_ENROLLMENT_SUCCESS = {
    CONTINUE: 'Continue',
    SUBTITLE:
        "You've successfully set up the METHOD method. You'll be asked for a code from your app each time you sign in.",
    TITLE: 'MFA is Now Activated',
};

/**
 * The copy of the MFA recovery screen, the screen a user who cannot produce a
 * code lands on from the verify identity screen.
 *
 * NOTE: the email verification option is display only, `TOTP` is the only
 * method the API supports, so the option is rendered disabled behind the
 * `EMAIL_BADGE` and only the support route is actionable.
 *
 * @type {Object}
 */
export const MFA_RECOVERY = {
    BACK: 'Back',
    EMAIL_BADGE: 'Coming Soon',
    EMAIL_DESCRIPTION: 'Receive a one-time code to your registered email address.',
    EMAIL_TITLE: 'Switch to Email Verification',
    RETURN_TO_SIGN_IN: 'Return to Sign-in',
    SUBTITLE: 'If you’ve lost access to your authentication method, here are your options.',
    SUPPORT_CTA: 'Submit a Request',
    SUPPORT_DESCRIPTION: 'A technical support agent can reset your MFA. Allow 1 business day.',
    SUPPORT_LABEL: 'Contact Technical Support',
    SUPPORT_TITLE: 'Contact Your Organization Admin, or Technical Support to Reset MFA',
    TITLE: 'Can’t Access Your Code?',
};

/**
 * The copy of the MFA "Enrollment failed" screen (Figma Screen 8), shown when
 * enrollment cannot be completed. `DEFAULT_MESSAGE` is the fallback of a visit
 * without a failure to describe in the 'mfa' state.
 *
 * @type {Object}
 */
export const MFA_ENROLLMENT_FAILED = {
    DEFAULT_MESSAGE: 'Enrollment could not be completed. Please try again.',
    RETURN_TO_SIGN_IN: 'Return to Sign-in',
    SUBTITLE:
        'Your organization requires multi-factor authentication before you can access your account.',
    TITLE: 'MFA Setup Required to Continue',
};

/**
 * The copy of the MFA setup screen, the screen a user without an enrolled method
 * chooses an authentication method on.
 *
 * NOTE: `EMAIL_DESCRIPTION` is the one description of the email method both the
 * full page and the dialog presentations render, they are the same screen of the
 * same flow so the wording is standardised.
 *
 * @type {Object}
 */
export const MFA_SETUP = {
    CANCEL: 'Cancel',
    CHOOSE_METHOD: 'Choose a method',
    CLOSE: 'Close',
    COMING_SOON_BADGE: 'Coming Soon',
    CONTINUE: 'Continue',
    EMAIL_DESCRIPTION: 'Receive a one-time code to your registered email address at each sign-in.',
    EMAIL_TITLE: 'Email',
    NO_METHODS_AVAILABLE:
        'No multi-factor authentication method is currently available for your account. Please return to sign-in and contact your organization admin.',
    RECOMMENDED_BADGE: 'Recommended',
    RETURN_TO_SIGN_IN: 'Return to Sign-in',
    SET_UP_MFA: 'Set Up MFA',
    SETTING_UP_MFA: 'Setting up MFA',
    SKIP: 'Skip for now',
    SUBTITLE:
        "Your organization's security policy requires all users to enable multi-factor authentication (MFA) before accessing Mineral Platform.",
    TITLE: 'Add Authentication Method',
    TOTP_DESCRIPTION: 'Use Google Authenticator, Authy, Microsoft Authenticator, or any TOTP app.',
    TOTP_TITLE: 'Authenticator App (TOTP)',
};

/**
 * The display copy of each MFA method, keyed by the method name of the API.
 *
 * The API answers with method names only, so the title, the description and the
 * badge of an option are owned by the front end. A method that is not listed
 * here has nothing to render and is left out of the option list altogether.
 *
 * `badge`            -> rendered on every presentation, it reports a method the
 *                       front end cannot take the user through yet.
 * `recommendedBadge` -> rendered on the dialog presentation only, as per design.
 *
 * @type {Object}
 */
export const MFA_SETUP_METHODS = {
    EMAIL: {
        badge: MFA_SETUP.COMING_SOON_BADGE,
        description: MFA_SETUP.EMAIL_DESCRIPTION,
        title: MFA_SETUP.EMAIL_TITLE,
    },
    TOTP: {
        description: MFA_SETUP.TOTP_DESCRIPTION,
        recommendedBadge: MFA_SETUP.RECOMMENDED_BADGE,
        title: MFA_SETUP.TOTP_TITLE,
    },
};

/**
 * The steps of the MFA enrollment, rendered above the option list.
 *
 * @type {Array}
 */
export const MFA_SETUP_STEPS = [
    { label: 'Choose a Method' },
    { label: 'Setup' },
    { label: 'Verify' },
];

/**
 * The index of the enrollment step the setup screen sits on.
 *
 * @type {number}
 */
export const MFA_SETUP_STEP_INDEX = 0;

/**
 * The index of the enrollment step the Scan QR Code screen sits on.
 *
 * @type {number}
 */
export const MFA_SCAN_QR_CODE_STEP_INDEX = 1;

/**
 * The index of the enrollment step the verify enrollment screen sits on.
 *
 * @type {number}
 */
export const MFA_VERIFY_ENROLLMENT_STEP_INDEX = 2;

/**
 * The index of the enrollment step the success screen sits on, i.e. one past
 * the last step so every step is rendered completed.
 *
 * @type {number}
 */
export const MFA_ENROLLMENT_SUCCESS_STEP_INDEX = MFA_SETUP_STEPS.length;

/**
 * The copy of the Scan QR Code screen, the screen the QR code and the key of a
 * started TOTP enrollment are read from.
 *
 * @type {Object}
 */
export const MFA_SCAN_QR_CODE = {
    BACK: 'Back',
    MANUAL_KEY_TITLE: 'Can’t scan? Enter this code manually',
    NEXT: 'Next',
    QR_TITLE: 'QR code of the setup key MANUAL_KEY',
    SUBTITLE:
        'Open your authenticator app and scan the image below to add your Mineral Platform account.',
    TITLE: 'Scan QR Code',
};

/**
 * The copy of the modal the signed in user adds an MFA method in from the
 * account settings.
 *
 * @type {Object}
 */
export const MFA_ADD_METHOD_MODAL = {
    BACK: 'Back',
    CONTINUE: 'Continue',
    VERIFY: 'Verify & Activate',
};

/**
 * The copy of the confirmation modal the signed in user removes an MFA
 * method with from the account settings.
 *
 * @type {Object}
 */
export const MFA_REMOVE_METHOD_MODAL = {
    CANCEL: 'Cancel',
    CONFIRM: 'Remove Method',
    DESCRIPTION:
        'This authentication method will be removed from your account. ' +
        'You won\u2019t be able to use this method to verify your identity. ' +
        'You can always add it back later from your settings. Do you want to continue?',
    TITLE: 'Remove Authentication Method',
};

/**
 * The HTTP status the API answers an enrollment that conflicts with the state
 * of the user with, e.g. a method that is already active.
 *
 * @type {number}
 */
export const MFA_CONFLICT_STATUS = 409;

/**
 * The number of characters of a group of the setup key, which is grouped for
 * display alone: authenticator apps accept the key with or without the spaces.
 *
 * @type {number}
 */
export const MFA_SECRET_GROUP_LENGTH = 4;

/**
 * The presentations of the method selection.
 * `PAGE`   -> the full page sign in gate, it cannot be dismissed.
 * `DIALOG` -> the dialog of the account settings, it can be dismissed.
 *
 * @type {Object}
 */
export const MFA_SETUP_VARIANTS = {
    DIALOG: 'dialog',
    PAGE: 'page',
};

/**
 * The route of the public technical support form the recovery screen sends the
 * user to, it is opened in its own tab so the MFA challenge is left untouched.
 *
 * @type {string}
 */
export const MFA_SUPPORT_ROUTE = '/auth/tech-support';

/**
 * The `errorCode` values, they are strings in the API responses, the verify API
 * answers a failed verification with.
 *
 * @type {Object}
 */
export const MFA_ERROR_CODES = {
    ENROLLMENT_IN_PROGRESS: '1060',
    ENROLLMENT_NOT_ALLOWED: '1069',
    INVALID_CODE: '1062',
    INVALID_TOKEN: '1061',
    MAX_ATTEMPTS: '1075',
    METHOD_ALREADY_ACTIVE: '1068',
    SESSION_EXPIRED: '1065',
    TOKEN_REPLAYED: '1063',
    TOKEN_REVOKED: '1076',
};

/**
 * The error states of the verify identity screen.
 * `invalidCode`    -> the code was rejected, the user can try again.
 * `maxAttempts`    -> every attempt of the challenge was used up.
 * `networkError`   -> the request never reached the API, or the API failed, so
 *                     the typed code was never evaluated.
 * `sessionExpired` -> the MFA token is gone, expired, replayed or revoked.
 *
 * @type {Object}
 */
export const MFA_ERROR_TYPES = {
    INVALID_CODE: 'invalidCode',
    MAX_ATTEMPTS: 'maxAttempts',
    NETWORK_ERROR: 'networkError',
    SESSION_EXPIRED: 'sessionExpired',
};

/**
 * The error state of the screen for each `errorCode` of the verify API.
 * Any code that is not listed here is rendered with the message of the API
 * itself and leaves the screen in its default, retryable, state.
 *
 * @type {Object}
 */
export const MFA_ERROR_TYPE_BY_CODE = {
    [MFA_ERROR_CODES.INVALID_CODE]: MFA_ERROR_TYPES.INVALID_CODE,
    [MFA_ERROR_CODES.INVALID_TOKEN]: MFA_ERROR_TYPES.SESSION_EXPIRED,
    [MFA_ERROR_CODES.MAX_ATTEMPTS]: MFA_ERROR_TYPES.MAX_ATTEMPTS,
    [MFA_ERROR_CODES.SESSION_EXPIRED]: MFA_ERROR_TYPES.SESSION_EXPIRED,
    [MFA_ERROR_CODES.TOKEN_REPLAYED]: MFA_ERROR_TYPES.SESSION_EXPIRED,
    [MFA_ERROR_CODES.TOKEN_REVOKED]: MFA_ERROR_TYPES.SESSION_EXPIRED,
};

/**
 * The error states the user cannot recover from on the screen itself, they
 * have to start over from the login screen.
 *
 * @type {Array}
 */
export const MFA_TERMINAL_ERROR_TYPES = [
    MFA_ERROR_TYPES.MAX_ATTEMPTS,
    MFA_ERROR_TYPES.SESSION_EXPIRED,
];

/**
 * The message rendered when the code is rejected, the user can try again.
 *
 * @type {string}
 */
export const MFA_INVALID_CODE_ERROR = 'The code you entered is invalid. Please try again.';

/**
 * The message rendered when the MFA session is no longer usable.
 *
 * @type {string}
 */
export const MFA_SESSION_EXPIRED_ERROR = 'Your session has expired. Please sign in again.';

/**
 * The message rendered when the verification request never reached the API, or
 * the API answered with a server error, i.e. the code was never evaluated.
 *
 * @type {string}
 */
export const MFA_NETWORK_ERROR =
    'We couldn’t reach the server. Please check your connection and try again.';

/**
 * The message rendered for a failure that carries no message of its own, so the
 * screen never falls back to a raw client error string.
 *
 * @type {string}
 */
export const MFA_GENERIC_ERROR =
    'Something went wrong and we couldn’t verify your code. Please try again.';

/**
 * The lowest HTTP status the screen handles the same way as a request that
 * never reached the API.
 *
 * @type {number}
 */
export const MFA_SERVER_ERROR_STATUS = 500;

/**
 * The `status` the register API answers a started enrollment with.
 *
 * @type {string}
 */
export const MFA_ENROLLMENT_STATUS = 'SETUP_REQUIRED';

/**
 * The `errorCode` values the register API answers a refused enrollment with,
 * i.e. the failures the user cannot get past by trying again.
 *
 * @type {Array}
 */
export const MFA_ENROLLMENT_TERMINAL_ERROR_CODES = [
    MFA_ERROR_CODES.ENROLLMENT_IN_PROGRESS,
    MFA_ERROR_CODES.ENROLLMENT_NOT_ALLOWED,
    MFA_ERROR_CODES.INVALID_TOKEN,
    MFA_ERROR_CODES.METHOD_ALREADY_ACTIVE,
    MFA_ERROR_CODES.TOKEN_REVOKED,
];

/**
 * The `errorCode` values the verify API answers a failed enrollment verification
 * with that the user cannot get past by trying again, on top of the ones every
 * verification treats as terminal.
 *
 * NOTE: a policy that no longer allows enrolling (`1069`) is terminal here alone:
 * an enrolled user verifying a code is not enrolling, so the sign in verification
 * keeps it retryable.
 *
 * @type {Array}
 */
export const MFA_VERIFY_ENROLLMENT_TERMINAL_ERROR_CODES = [MFA_ERROR_CODES.ENROLLMENT_NOT_ALLOWED];

/**
 * The message rendered when the enrollment could not be started and the API
 * answered with no message of its own.
 *
 * NOTE: every failure the API describes itself is rendered with the `message` of
 * the response, the front end keeps no copy of its own per error code.
 *
 * @type {string}
 */
export const MFA_ENROLLMENT_GENERIC_ERROR =
    'Something went wrong and we couldn’t start the setup. Please try again.';

/**
 * The id of the node holding the enrollment error of the setup screen.
 *
 * @type {string}
 */
export const MFA_ENROLLMENT_ERROR_MESSAGE_ID = 'mfa-enrollment-error-message';

/**
 * The error states the code inputs are not cleared for, the typed code was
 * never evaluated so the user can retry it as it is.
 *
 * @type {Array}
 */
export const MFA_CODE_PRESERVED_ERROR_TYPES = [MFA_ERROR_TYPES.NETWORK_ERROR];

/**
 * The counter rendered above the inputs after a rejected code. It is built out
 * of three parts so the attempts left are emphasised, as per the design, and
 * `ATTEMPTS_LEFT` is replaced with the `remainingAttempts` of the response.
 *
 * @type {Object}
 */
export const MFA_ATTEMPTS_REMAINING = {
    ATTEMPTS_PLURAL: 'ATTEMPTS_LEFT attempts',
    ATTEMPTS_SINGULAR: 'ATTEMPTS_LEFT attempt',
    PREFIX: 'You have ',
    SUFFIX: ' left to enter the correct code.',
};

/**
 * The id of the node holding the verification error, the code inputs point at
 * it so assistive technology reads the reason of the failure.
 *
 * @type {string}
 */
export const MFA_ERROR_MESSAGE_ID = 'mfa-verify-identity-error';

/**
 * The id of the node holding the attempts left in the challenge, the code inputs
 * point at it so assistive technology reads it along with the error.
 *
 * @type {string}
 */
export const MFA_ATTEMPTS_MESSAGE_ID = 'mfa-verify-identity-attempts';

/**
 * The duration, in milliseconds, of the animation the code inputs play when the
 * typed code is rejected.
 *
 * @type {number}
 */
export const MFA_INVALID_CODE_ANIMATION_DURATION = 300;
